import axios from 'axios';

import accessLocalStorage from './accessLocalStorage';
import { runRequest } from './apiResult';
import { legacyApiUrl } from './legacyApiBase';

const SURVEYS_PATH = 'api/config/csat/surveys';

const request = async (method, path, body) => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  const config = { headers: { Authorization: token } };
  const url = `${ipRequest}${path}`;
  if (method === 'get') return axios.get(url, config);
  if (method === 'put') return axios.put(url, body, config);
  if (method === 'delete') return axios.delete(url, config);
  return axios.post(url, body, config);
};

const tenantQuery = (tenant) => (tenant ? `tenant=${encodeURIComponent(tenant)}` : '');

const withQuery = (path, params) => {
  const query = params.filter(Boolean).join('&');
  return query ? `${path}?${query}` : path;
};

const bodyData = (response) => (response && response.data ? response.data.data : null);

const withReload = (response) => ({
  survey: bodyData(response),
  dialplanReloaded: !(response && response.data && response.data.dialplanReloaded === false),
});

export const getCsatSurveys = async (tenant) =>
  runRequest(() => request('get', withQuery(SURVEYS_PATH, [tenantQuery(tenant)])), {
    fallback: 'Erro ao carregar pesquisas.',
    select: (response) => {
      const data = bodyData(response);
      return Array.isArray(data) ? data : [];
    },
  });

export const getCsatSurvey = async (id, tenant) =>
  runRequest(() => request('get', withQuery(`${SURVEYS_PATH}/${encodeURIComponent(id)}`, [tenantQuery(tenant)])), {
    fallback: 'Erro ao carregar pesquisa.',
    select: bodyData,
  });

export const createCsatSurvey = async (payload) =>
  runRequest(() => request('post', SURVEYS_PATH, payload), {
    fallback: 'Erro ao salvar pesquisa.',
    select: withReload,
  });

export const updateCsatSurvey = async (id, payload) =>
  runRequest(() => request('put', `${SURVEYS_PATH}/${encodeURIComponent(id)}`, payload), {
    fallback: 'Erro ao salvar pesquisa.',
    select: withReload,
  });

export const deleteCsatSurvey = async (id, tenant) =>
  runRequest(() => request('delete', withQuery(`${SURVEYS_PATH}/${encodeURIComponent(id)}`, [tenantQuery(tenant)])), {
    fallback: 'Erro ao excluir pesquisa.',
    select: withReload,
  });

export const getCsatAudios = async (tenant) =>
  runRequest(() => request('get', withQuery('api/config/dialplan/audios/list', [tenantQuery(tenant)])), {
    fallback: 'Erro ao carregar áudios.',
    select: (response) => {
      const data = bodyData(response);
      return Array.isArray(data) ? data.filter((audio) => audio.type === 'ura' || audio.type === 'msg') : [];
    },
  });

export const getCsatBindings = async (tenant) =>
  runRequest(() => request('get', withQuery('api/config/queues/csat-bindings', [tenantQuery(tenant)])), {
    fallback: 'Erro ao carregar a pesquisa da fila.',
    select: (response) => {
      const data = bodyData(response);
      return Array.isArray(data) ? data : [];
    },
  });

export const setCsatBinding = async ({ tenant, queueName, surveyId }) =>
  runRequest(() => request('put', 'api/config/queues/csat-binding', { tenant, queueName, surveyId }), {
    fallback: 'Não foi possível aplicar a pesquisa à fila.',
    select: (response) => (response ? response.data : null),
  });

const reportParams = (filters) => {
  const params = new URLSearchParams();
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.append(key, String(value));
  });
  return params.toString();
};

export const getCsatSummary = async (filters) =>
  runRequest(() => request('get', withQuery('api/csat/summary', [reportParams(filters)])), {
    fallback: 'Não foi possível carregar os dados da pesquisa de satisfação.',
    select: bodyData,
  });

export const getCsatRanking = async (filters) =>
  runRequest(() => request('get', withQuery('api/csat/ranking', [reportParams(filters)])), {
    fallback: 'Não foi possível carregar o ranking.',
    select: bodyData,
  });

const saveBlob = (data, fileName) => {
  const objectUrl = window.URL.createObjectURL(data instanceof Blob ? data : new Blob([data]));
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
};

const blobRequest = async (url) => {
  const { token } = await accessLocalStorage.getUserLocalStorage();
  return axios.get(url, { headers: { Authorization: token }, responseType: 'blob' });
};

export const downloadCsatCsv = async (filters) =>
  runRequest(async () => {
    const { ipRequest } = await accessLocalStorage.getUserLocalStorage();
    const response = await blobRequest(`${ipRequest}${withQuery('api/csat/responses.csv', [reportParams(filters)])}`);
    saveBlob(response.data, `csat_${filters.from}_${filters.to}.csv`);
    return response;
  }, { fallback: 'Não foi possível exportar as respostas.', select: () => null });

export const downloadCsatRecording = async (protocol) =>
  runRequest(async () => {
    const { ipRequest } = await accessLocalStorage.getUserLocalStorage();
    const response = await blobRequest(legacyApiUrl(ipRequest, `api/download/file/${encodeURIComponent(protocol)}`));
    saveBlob(response.data, `${protocol}.wav`);
    return response;
  }, { fallback: 'Não foi possível baixar a gravação.', select: () => null });

export const getCsatResponses = async (filters) =>
  runRequest(() => request('get', withQuery('api/csat/responses', [reportParams(filters)])), {
    fallback: 'Não foi possível carregar os dados da pesquisa de satisfação.',
    select: bodyData,
  });
